//Copyright Open Trade Technology Ltd. 2023. All rights reserved.

import { EthAddr, TxHash } from '@shared/SchemaTypes'
import { Contract, Signer } from 'ethers'

import { ActivityEnum, AlertLevelEnum, PoolTypeEnum, RoleEnum } from '@shared/Enums'

import { poolActions } from '@react/redux/poolSlice'

import { logOT } from '@shared/loggingShared'

import { ThunkAction, UnknownAction } from '@reduxjs/toolkit'
import { IPoolBaseAbi, PoolControllerDynamicAbi, PoolDynamicAbi } from '@shared/Abis'

import { assertRole, dispatchError, OtStoreState } from '@react/redux/store'

import { otAssert } from '@shared/conversions'

import { alertActions } from '@react/redux/alertSlice'
import { ETH_ADDR_ZERO } from '@shared/SchemaTypes'
import { txUUID, wsServerActionPoolTX, wsServerActionPoolTXRequest } from '@shared/WsServerActions'
import { PoolControllerDynamic, PoolDynamic } from '@typechain-types/index'
import { UserConnection } from '@shared/DeploymentConfigTypes'

export const dynamicCreditDebitTx =
  (props: {
    poolAddr: EthAddr

    lenderAddr: EthAddr
    debit: string | bigint | number
    credit: string | bigint | number
  }): ThunkAction<void, OtStoreState, unknown, UnknownAction> =>
  async (dispatch, getState): Promise<void> => {
    const { poolAddr, lenderAddr, debit, credit } = props
    const activity = ActivityEnum.POOL_ACTIVATE
    try {
      logOT.debug(activity)
      const { webSocket } = getState().chainState
      const { chainConfigurationName } = getState().chainState.chainConfiguration
      const { roleEnum, signer } = getState().chainState.userConnection as UserConnection

      assertRole(roleEnum, RoleEnum.POOL_ADMIN)
      otAssert(signer, 'signer is not defined')

      await dynamicCreditDebit(
        webSocket,
        signer as Signer,
        poolAddr,
        lenderAddr,
        BigInt(debit),
        BigInt(credit),
        chainConfigurationName
      )

      dispatch(poolActions.setExchangeRateSuccess({ poolAddr }))
    } catch (e: any) {
      logOT.debug('dynamicCreditDebit error ', e)
      dispatch(poolActions.setExchangeRateFail({}))
      dispatch(
        alertActions.addAlert({
          title: 'Apply credit/debit failed.',
          description: 'Please wait and try again',
          createdAt: Date.now() / 1000,
          dueDateTime: Date.now() / 1000,
          txHash: '',
          sourceObjectAddr: ETH_ADDR_ZERO,
          alertLevel: AlertLevelEnum.ERROR,
        })
      )
      await dispatchError(dispatch, e, activity, 1037)
    }
  }
export const dynamicCreditDebit = async (
  webSocket: WebSocket | undefined,
  poolAdminSigner: Signer,
  poolAddr: EthAddr,
  lenderAddr: EthAddr,
  debit: bigint,
  credit: bigint,
  chainConfigurationName: string
): Promise<void> => {
  logOT.debug('activatePool ')
  const uuid = txUUID()
  logOT.debug('dynamicCreditDebit', poolAddr, lenderAddr, debit, credit)
  webSocket?.send(wsServerActionPoolTXRequest(ActivityEnum.POOL_DEBIT_CREDIT, poolAddr, uuid, chainConfigurationName))

  const poolBaseContract = new Contract(poolAddr, IPoolBaseAbi, poolAdminSigner)
  const poolType = Number(await poolBaseContract.poolType())

  if (poolType === PoolTypeEnum.Dynamic) {
    const poolContract = new Contract(poolAddr, PoolDynamicAbi, poolAdminSigner) as unknown as PoolDynamic
    const poolControllerAddr = await poolContract.poolControllerDynamic()

    otAssert(poolControllerAddr)

    logOT.debug('dynamicCreditDebit', poolControllerAddr)

    const poolControllerContract = new Contract(
      poolControllerAddr,
      PoolControllerDynamicAbi,
      poolAdminSigner
    ) as unknown as PoolControllerDynamic

    logOT.debug('dynamicCreditDebit before credit', poolAddr, lenderAddr, debit, credit)
    const tx2 = await poolControllerContract.connect(poolAdminSigner).addAdjustmentAmount(lenderAddr, debit, credit)
    logOT.debug('dynamicCreditDebit after credit', poolAddr, lenderAddr, debit, credit)

    webSocket?.send(
      wsServerActionPoolTX(ActivityEnum.POOL_DEBIT_CREDIT, poolAddr, uuid, tx2.hash as TxHash, chainConfigurationName)
    )

    await tx2.wait(1)
    const assets = await poolContract.assetBalanceOf(lenderAddr)
    logOT.debug('dynamicCreditDebit after credit', poolAddr, lenderAddr, assets)

    const accountState = await poolContract.getPoolDynamicAccountState(lenderAddr)
    logOT.debug('dynamicCreditDebit after credit', poolAddr, lenderAddr, accountState)
  } else {
    throw new Error('Unknown pool type')
  }
}
